/**
 * WHICH COMPANY THE ADDRESS NAMES — #218.
 *
 * `app.flightdeck.build/trinre/` means TRINRE. Before this, every company
 * answered on the same address and which one you were in lived in your sign-in
 * and nowhere else — so nobody could send Richard Robinson a link that would
 * get him to the second company he belonged to. There was no such link.
 *
 * READ, NEVER TRUSTED, exactly like workspace.ts beside it. This puts a name in
 * a header; `my_tenant()` (migration 0066) decides what it means, and honours it
 * only where a real membership exists. An address naming a company you are not
 * in gets you nothing, so the worst a wrong value here can do is show the wrong
 * name on a screen.
 *
 * THE OLD ADDRESS KEEPS WORKING, PERMANENTLY. That is a requirement rather than
 * a courtesy: `signInTarget()` returns the bare origin, so it is the address
 * inside every sign-in link and every invitation ever sent, and it is what
 * Supabase holds in its redirect allow-list. No company in the path means the
 * behaviour there has always been — the database falls back to the sign-in, then
 * to the oldest membership — and the address quietly corrects itself afterwards.
 */

/**
 * FIRST SEGMENTS THAT ARE NOT A COMPANY.
 *
 * `sign` is the one that matters today: approval emails send people with no
 * account to `/sign/<token>`, read in main.tsx before the app mounts. Reading
 * that as a company called "sign" would break every approval link at once.
 *
 * THE DATABASE HOLDS THE SAME LIST — `reserved_slugs` in 0066, which refuses
 * these names when a company is CREATED. This copy exists because the browser
 * has to decide what a path means before it has asked anything.
 *
 * THE TWO ARE HELD IDENTICAL by test-the-company-in-the-path.ts, in both
 * directions, and both directions are a real fault. A word here that the
 * database allows means a genuine customer gets an address the app refuses to
 * read. A word in the database that is missing here means the app reads some
 * future page of its own — `/board/...` was exactly this — as a company, and
 * quietly strips it.
 */
export const NOT_A_COMPANY = [
  'sign', 'board', 'admin', 'api', 'auth', 'assets', 'static', 'app', 'www',
  'new', 'login', 'signin', 'signup', 'reset', 'invite', 'account', 'settings',
  'support', 'help', 'docs', 'health',
];

/** A company address is lowercase letters, digits and single hyphens. */
const SHAPE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * The company named in a path, or nothing.
 *
 * Nothing is the ordinary answer and always will be: it is what every bookmark
 * and every emailed link says, and what a single-company customer sees forever.
 */
export function companyIn(path: string): string {
  const first = path.split('/').filter(Boolean)[0] ?? '';
  if (!first || first.length > 40) return '';
  if (!SHAPE.test(first)) return '';
  if (NOT_A_COMPANY.includes(first)) return '';
  return first;
}

/** The company this page is for, read once so every caller agrees. */
export function companyInAddress(): string {
  if (typeof window === 'undefined') return '';
  return companyIn(window.location.pathname);
}

/** The rest of the address, with any company taken off the front. */
export function pathWithoutCompany(path: string): string {
  const company = companyIn(path);
  if (!company) return path;
  const rest = path.slice(company.length + 1);
  return rest.startsWith('/') ? rest : `/${rest}`;
}

/** Where a company's board lives, for a link somebody can send. */
export function addressFor(slug: string, rest = '/'): string {
  const tail = rest.startsWith('/') ? rest : `/${rest}`;
  return `/${slug}${tail === '/' ? '' : tail}`;
}

/**
 * PUT THE COMPANY IN THE ADDRESS WITHOUT RELOADING — the seamless half.
 *
 * `replaceState` rather than a redirect, and rather than `pushState`. A redirect
 * would cost a round trip and a flash of white on every visit anybody makes from
 * a bookmark. `pushState` would put the address-without-a-company into the back
 * history, so pressing Back would appear to do nothing.
 *
 * Called once the app knows which company it actually landed in, so what is on
 * screen, what the sidebar says, and what somebody could copy out of the address
 * bar are the same thing. A bookmark upgrades itself the first time it is used.
 */
export function settleTheAddress(slug: string): void {
  if (typeof window === 'undefined' || !slug) return;
  if (companyIn(window.location.pathname)) return; // already says it
  if (NOT_A_COMPANY.includes(window.location.pathname.split('/').filter(Boolean)[0] ?? '')) return;
  const { pathname, search, hash } = window.location;
  const settled = addressFor(slug, pathname) + search + hash;
  window.history.replaceState(window.history.state, '', settled);
}

/** A company somebody belongs to, as much of it as the choosing needs. */
export interface Mine {
  id: string;
  slug: string;
  suspended: boolean;
}

/**
 * WHICH COMPANY THE APP IS IN — the same order `my_tenant()` uses, in one place.
 *
 * WHY IT IS A FUNCTION AND NOT TWO OPINIONS. It was two: App.tsx read the
 * address, `WorkspaceSwitcher` read the sign-in token, and the database read
 * the address and then the token. Three answers to one question, and on 24
 * August 2026 they disagreed in front of Vinnie — the corner named the
 * workspace he administers while every query was answered as a company where he
 * is a viewer. Two copies of a rule are a rule that will drift; this is the
 * rule, and both callers ask it.
 *
 * `'ask'` IS A REAL ANSWER, and the change #247 is about. The database falls
 * back to the OLDEST membership when nothing names a company — inherited from
 * migration 0010, invisible while everybody had one company, and arbitrary the
 * moment somebody has two. The app no longer takes that answer: where there is
 * a genuine choice it says so and the person makes it.
 *
 * WHAT THE SIGN-IN TOKEN REMEMBERS IS DELIBERATELY NOT CONSULTED, and this is
 * the one place the app answers differently from `my_tenant()`. The token
 * carries whichever company was last chosen, on any day, in any browser; it is
 * never cleared. Asking it first meant that anybody who had ever switched
 * company was never asked again — which is every account that has ever used the
 * switcher, including both of Vinnie's. The chooser shipped, was correct, and
 * could not appear. He tested it on 24 August 2026 and reported, rightly, that
 * it did nothing at all.
 *
 * THE DIVERGENCE COSTS NOTHING, because a choice is followed by a reload at an
 * address that names the company, and that address is what the request header
 * carries. The database and the app agree from that moment on; they differ only
 * on the bare address, where the app asks and the database would have guessed.
 *
 * SUSPENDED COMPANIES ARE NOT CANDIDATES, because `my_tenant()` will not put
 * anybody in one. Counting them here would offer a door that does not open.
 */
export function whichCompany(named: string, mine: Mine[]): Mine | 'ask' | 'none' {
  const live = mine.filter((w) => !w.suspended);
  // The company the ADDRESS names, if they are really in it — as the database does.
  const byAddress = named ? live.find((w) => w.slug === named) : undefined;
  if (byAddress) return byAddress;
  if (live.length === 1) return live[0];
  return live.length ? 'ask' : 'none';
}
